"use client";

import { MonitorX } from "lucide-react";
import type { MediaCapability } from "mediatools";
import { useTranslations } from "next-intl";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

/**
 * Shown when the browser lacks a capability a media tool needs. These tools run
 * 100% on-device with no ffmpeg.wasm fallback, so the honest answer is "use a
 * current browser" rather than silently degrading.
 */
export function UnsupportedBrowserNotice({ missing }: { missing: MediaCapability[] }) {
  const t = useTranslations("mediaTools.unsupported");
  const capabilityLabels: Record<MediaCapability, string> = {
    webAudio: t("capabilities.webAudio"),
    audioDecode: t("capabilities.audioDecode"),
    audioEncodeOpus: t("capabilities.audioEncodeOpus"),
    audioEncodeAac: t("capabilities.audioEncodeAac"),
    videoDecode: t("capabilities.videoDecode"),
    videoEncode: t("capabilities.videoEncode"),
    videoEncodeVp9: t("capabilities.videoEncodeVp9"),
    webgpuF16: t("capabilities.webgpuF16"),
  };

  return (
    <Alert variant="destructive" className="my-6">
      <MonitorX className="h-4 w-4" />
      <AlertTitle>{t("title")}</AlertTitle>
      <AlertDescription>
        <p>
          {t("description", {
            capabilities: missing.map((capability) => capabilityLabels[capability]).join(", "),
          })}
        </p>
        <p className="mt-2">{t("tryBrowser")}</p>
      </AlertDescription>
    </Alert>
  );
}
